// SubcategoryBar.jsx - Fixed version
import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { SubCategory } from "../../../services/api/category";

const SubcategoryBar = ({
  parentCategoryId,
  onSubcategorySelect,
  selectedSubcategoryId,
  onSubSubcategorySelect,
  selectedSubSubcategoryId,
  onSubSubcategoriesLoaded, // NEW: callback to lift sub-subcategories to Header
}) => {
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Track the last fetched parent to avoid duplicate fetches
  const lastFetchedParentId = useRef(null);
  // Track if we already auto-selected for this parentCategoryId render
  const autoSelectedRef = useRef(false);

  useEffect(() => {
    if (!parentCategoryId) {
      setSubcategories([]);
      lastFetchedParentId.current = null;
      autoSelectedRef.current = false;
      return;
    }

    // Always re-fetch when parentCategoryId changes (including same category re-click
    // because Header passes a new `key` prop which remounts this component)
    const fetchSubcategories = async () => {
      setLoading(true);
      autoSelectedRef.current = false;
      try {
        const response = await SubCategory(parentCategoryId);
        const list = response?.data?.data || [];
        setSubcategories(list);
        lastFetchedParentId.current = parentCategoryId;

        if (list.length === 0) return;

        // Determine which subcategory to activate
        let targetSub = null;
        if (selectedSubcategoryId) {
          targetSub = list.find((sub) => sub.id === selectedSubcategoryId);
        }
        // If no match found (or no selectedSubcategoryId), use first
        if (!targetSub) {
          targetSub = list[0];
        }

        autoSelectedRef.current = true;
        onSubcategorySelect(targetSub);

        // Fetch sub-subcategories for the target
        if (targetSub.hasChildren) {
          await fetchSubSubcategories(targetSub.id, true);
        } else {
          // Clear sub-subcategories
          if (onSubSubcategoriesLoaded) onSubSubcategoriesLoaded([]);
          if (onSubSubcategorySelect) onSubSubcategorySelect(null);
        }
      } catch (err) {
        console.error("Error fetching subcategories:", err);
        setSubcategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubcategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCategoryId]);
  // Note: intentionally NOT including selectedSubcategoryId in deps to avoid loops.
  // The initial restore is handled by checking selectedSubcategoryId inside the effect.

  const fetchSubSubcategories = async (subcategoryId, isInitialLoad = false) => {
    if (!subcategoryId) {
      if (onSubSubcategoriesLoaded) onSubSubcategoriesLoaded([]);
      if (onSubSubcategorySelect) onSubSubcategorySelect(null);
      return;
    }
    try {
      const response = await SubCategory(subcategoryId);
      const list = response?.data?.data || [];

      // Lift list to Header for the chip bar
      if (onSubSubcategoriesLoaded) onSubSubcategoriesLoaded(list);

      if (list.length === 0) {
        if (onSubSubcategorySelect) onSubSubcategorySelect(null);
        return;
      }

      if (isInitialLoad) {
        // On initial load, restore selectedSubSubcategoryId or pick first
        let targetSubSub = null;
        if (selectedSubSubcategoryId) {
          targetSubSub = list.find((s) => s.id === selectedSubSubcategoryId);
        }
        if (!targetSubSub) targetSubSub = list[0];
        if (onSubSubcategorySelect) onSubSubcategorySelect(targetSubSub);
      } else {
        // On user-initiated subcategory change, always pick first
        if (onSubSubcategorySelect) onSubSubcategorySelect(list[0]);
      }
    } catch (err) {
      console.error("Error fetching sub-subcategories:", err);
      if (onSubSubcategoriesLoaded) onSubSubcategoriesLoaded([]);
      if (onSubSubcategorySelect) onSubSubcategorySelect(null);
    }
  };

  const handleSubcategoryClick = async (subcategory) => {
    // Clear sub-subcategories immediately for clean state
    if (onSubSubcategoriesLoaded) onSubSubcategoriesLoaded([]);
    if (onSubSubcategorySelect) onSubSubcategorySelect(null);

    onSubcategorySelect(subcategory);

    if (subcategory.hasChildren) {
      await fetchSubSubcategories(subcategory.id, false);
    } else {
      if (onSubSubcategoriesLoaded) onSubSubcategoriesLoaded([]);
      if (onSubSubcategorySelect) onSubSubcategorySelect(null);
    }
  };

  if (!parentCategoryId) return null;

  return (
    <Box
      sx={{
        width: 100,
        height: "100%",
        background: "#FEFEFE",
        borderRight: "1px solid #eaeef5",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        flexShrink: 0,
        py: 0.5,
        "&::-webkit-scrollbar": { width: "3px" },
        "&::-webkit-scrollbar-track": { background: "#f5f5f5" },
        "&::-webkit-scrollbar-thumb": { background: "#e0e0e0", borderRadius: 10 },
      }}
    >
      {loading
        ? Array(6).fill(0).map((_, i) => (
            <Box key={i} sx={{ p: 1.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 58, height: 58, borderRadius: "50%", background: "#e8edf4" }} />
              <Box sx={{ width: 60, height: 10, borderRadius: 4, background: "#e8edf4" }} />
            </Box>
          ))
        : subcategories.map((sub) => {
            const isActive = selectedSubcategoryId === sub.id;
            return (
              <Box
                key={sub.id}
                onClick={() => handleSubcategoryClick(sub)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.5,
                  py: 1,
                  cursor: "pointer",
                  borderLeft: `3px solid ${isActive ? "#f7d261" : "transparent"}`,
                  background: isActive ? "#fff" : "transparent",
                  transition: "all 0.18s ease",
                  "&:hover": { background: isActive ? "#fff" : "#eef2f9" },
                }}
              >
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: "50%",
                    background: "#fff",
                    border: `2px solid ${isActive ? "#f7d261" : "#e0e0e0"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    position: "relative",
                    transition: "border-color 0.18s ease",
                  }}
                >
                  {sub.imageUrl ? (
                    <img
                      src={sub.imageUrl}
                      alt={sub.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: 22 }}>📁</Typography>
                  )}
                  {sub.hasChildren && (
                    <Box
                      sx={{
                        position: "absolute", bottom: -2, right: -2,
                        width: 18, height: 18, borderRadius: "50%",
                        background: "#0f4c81",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 10, color: "white",
                      }}
                    >
                      ▶
                    </Box>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    fontWeight: isActive ? 700 : 600,
                    color: isActive ? "#0f4c81" : "#666",
                    textAlign: "center",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                    maxWidth: 90,
                  }}
                >
                  {sub.name}
                </Typography>
              </Box>
            );
          })}
    </Box>
  );
};

export default SubcategoryBar;